import { useCallback, useEffect, useMemo, useState } from "react";
import DataQueryParams, {
    DataQueryParamsInterface,
    FilterParams,
    FilterValue,
    PaginationParamsWithTotalInterface,
    SortBy,
    SortDir
} from "../../model/DataQueryParams";
import { PaginationProps } from "../components/Pagination";
import { useDebouncedCallback } from "use-debounce";
import { DEBOUNCE } from "../view-tools";
import { SortableTableHeaderProps } from "../components/SortableTableHeader";
import { arrayUnique, arrayValuesBetween } from "../../utils/array-utils";

const useTable = <FiltersT extends FilterParams = FilterParams>(initialParams?: {
    dataQueryParams?: DataQueryParamsInterface<FiltersT>,
    selectedIds?: Array<RowId>
}) => {
    const [searchValue, setSearchValue] = useState(initialParams?.dataQueryParams?.search || '');
    const [dataQueryParams, _setDataQueryParams] = useState<DataQueryParams<FiltersT>>(new DataQueryParams<FiltersT>(initialParams?.dataQueryParams));
    const [total, setTotal] = useState<number>(0);

    const setDataQueryParams = (newDataQueryParamsOrFun: DataQueryParams<FiltersT> | ((newDataQueryParams: DataQueryParams<FiltersT>) => DataQueryParams<FiltersT>)) => {
        if (typeof newDataQueryParamsOrFun === 'function') {
            _setDataQueryParams(currentDataQueryParams => {
                const newDataQueryParams = newDataQueryParamsOrFun(currentDataQueryParams);
                if (JSON.stringify(currentDataQueryParams) !== JSON.stringify(newDataQueryParams)) {
                    return newDataQueryParams;
                }
                return currentDataQueryParams;
            });
        } else {
            _setDataQueryParams(currentDataQueryParams => {
                const newDataQueryParams = newDataQueryParamsOrFun;
                if (JSON.stringify(currentDataQueryParams) !== JSON.stringify(newDataQueryParams)) {
                    return new DataQueryParams<FiltersT>(newDataQueryParams);
                }
                return currentDataQueryParams;
            });
        }
    }

    const [selectedIds, setSelectedIds] = useState<Array<RowId>>(initialParams?.selectedIds || []);

    const ordinalNumberStart = useMemo(
        () => dataQueryParams.pagination.startIndex || 1,
        [dataQueryParams.pagination.startIndex]
    );

    const paginationProps: PaginationProps = useMemo(() => ({
        ...dataQueryParams.pagination,
        total,
        onPageChange: page => setDataQueryParams(dataQueryParams.withPage(page)),
        onSizeChange: size => setDataQueryParams(dataQueryParams.withSize(size).withPage(1)),
    }), [dataQueryParams.pagination, total]);

    const onRowClick = useCallback((id: RowId) => {
        setSelectedIds(selectedIds =>
            selectedIds.includes(id)
                ? selectedIds.filter(selectedId => selectedId !== id)
                : [...selectedIds, id]
        )
    }, []);

    const onRowShiftClick = useCallback((id: RowId, allAvailableIds: Array<RowId>): RowId[] | null => {
        let selectedIds: RowId[] = [];

        setSelectedIds(currentSelectedIds => (() => {
            selectedIds = currentSelectedIds;
            return currentSelectedIds;
        })());

        const lastSelectedId = selectedIds.length > 0
            ? selectedIds[selectedIds.length - 1]
            : undefined;

        if (!lastSelectedId || !allAvailableIds.includes(lastSelectedId)) {
            return null;
        }

        const idsToSelect = arrayUnique(arrayValuesBetween(lastSelectedId, id, allAvailableIds));
        setSelectedIds(selectedIds => arrayUnique([...selectedIds, ...idsToSelect]));
        return idsToSelect;
    }, [selectedIds]);

    const onCheckAllClick = useCallback(
        (ids: Array<RowId>) => setSelectedIds(selectedIds => [...selectedIds, ...ids]
            .filter((id, idx, self) => idx === self.indexOf(id))),
        []
    );

    const onUncheckAllClick = useCallback(() => setSelectedIds([]), []);

    const onFilterChange: OnFilterChange<FiltersT> = useCallback(
        (name, value) =>
            setDataQueryParams(dataQueryParams => dataQueryParams.withFilter(name, value).withPage(1)),
        []
    );

    const onClearFiltersClick = useCallback(() => {
        setDataQueryParams(dataQueryParams => dataQueryParams.withNoFilters().withSearch(''));
        setSearchValue('');
    }, []);

    const onSearchChange: OnSearchChange = useCallback((value: string) => {
        setSearchValue(currentValue => {
            if (value !== currentValue) {
                debouncedOnSearchChange(value);
            }
            return value;
        })
    }, []);

    const debouncedOnSearchChange =
        useDebouncedCallback(
            (value: string) =>
                setDataQueryParams(dataQueryParams => dataQueryParams.withSearch(value).withPage(1)),
            DEBOUNCE
        );

    const isSelected = useCallback((id: RowId | string) => selectedIds.indexOf(id) !== -1, [selectedIds]);

    const onSortByChange = useCallback(
        (sortBy: SortBy) => {
            if (JSON.stringify(dataQueryParams.pagination.sort) !== JSON.stringify([sortBy])) {
                setDataQueryParams(dataQueryParams.withSortBy(sortBy).withPage(1))
            }
        },
        [dataQueryParams]
    );

    const [currentSortBy, setCurrentSortBy] = useState<SortBy | undefined>(
        initialParams?.dataQueryParams?.pagination?.sort[0]
    );
    const onSortableHeaderClick = useCallback((field: string) => {
        setCurrentSortBy(currentSortBy => {
            let dir = SortDir.DESC;
            if (currentSortBy?.field === field) {
                dir = currentSortBy.dir === SortDir.DESC ? SortDir.ASC : SortDir.DESC;
            }
            return { field, dir }
        });
    }, [])

    useEffect(() => {
        currentSortBy?.field.length && onSortByChange(currentSortBy)
    }, [currentSortBy])


    const sortableHeaderPropsFactory: SortableTableHeaderPropsFactory = useCallback(field => ({
        onClick: onSortableHeaderClick,
        field,
        currentSortBy
    }), [onSortableHeaderClick, currentSortBy])

    const isAnyFilterActive = useMemo(() => {
        if (searchValue.length > 0) {
            return true;
        }

        return Object.values(dataQueryParams.filters || {}).some(value => {
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (typeof value === 'string') {
                return value.length > 0;
            }
            if (typeof value === 'object') {
                return Object.keys(value).length > 0;
            }
            if (typeof value === 'number') {
                return value !== 0; //@todo: it's compatible with SelectField, but it's not a good solution
            }
            return value !== undefined && value !== null;
        });
    }, [dataQueryParams.filters, searchValue]);

    const setPagination = useCallback((pagination: PaginationParamsWithTotalInterface) => {
        setTotal(pagination.total || 0);
        setDataQueryParams(dataQueryParams => {
            return dataQueryParams.withPagination(pagination)
        })
    }, []);

    const clearSelectedIds = useCallback(() => setSelectedIds([]), []);

    const unselectIds = useCallback(
        (ids: RowId | RowId[]) => setSelectedIds(selectedIds =>
            selectedIds.filter(selectedId => Array.isArray(ids) ? !ids.includes(selectedId) : selectedId !== ids)
        ),
        []
    );

    return {
        isAnyFilterActive,
        isSelected,
        dataQueryParams,
        selectedIds,
        ordinalNumberStart,
        paginationProps,
        onRowClick,
        onFilterChange,
        onSearchChange,
        searchValue,
        onClearFiltersClick,
        onSortByChange,
        currentSortBy,
        onCheckAllClick,
        onUncheckAllClick,
        sortableHeaderPropsFactory,
        setDataQueryParams,
        setPagination,
        clearSelectedIds,
        unselectIds,
        setSelectedIds,
        onRowShiftClick,
    };
}
export default useTable;

export type SortableTableHeaderPropsFactory = (field: string) => SortableTableHeaderProps;
export type OnFilterChange<FiltersT extends object = any> = (name: keyof FiltersT, value: FilterValue) => void;
export type OnSearchChange = (value: string) => void;
type RowId = number | string;